/**
 * Fire: shared settings for the server simulation and the client renderer.
 *
 * The map is covered by a grid of square cells (cellSize units). A cell can hold poured fuel, be
 * kindling (about to catch), burning, or scorched. Fire never spreads on its own across the map:
 * it runs freely only along fuel; on grass it can only spend the limited "spread budget" of the
 * fire that started it; a fire's total size and the number of burning cells on the whole map are
 * capped. Cells are sent to clients in chunks of chunkCells x chunkCells as bitmasks.
 */
export const FireConfig = Object.freeze({
    /** game units per cell (a player is 4.5 units wide) */
    cellSize: 4,
    /** cells per chunk side: a chunk is 8 x 8 cells = 32 x 32 units, one network object */
    chunkCells: 8,
    /** the spread / burn-out step runs every this many server ticks (40 tps -> 5 Hz) */
    stepTicks: 8,

    /** hard caps: burning cells on the whole map, and cells one fire (one Molotov, one trail) may ever light */
    maxBurningCells: 1500,
    maxCellsPerSource: 140,

    /** a freshly lit cell flickers this long before it burns (warning, no damage) */
    kindleMs: 300,
    /** along poured fuel the flame front moves one cell per step: 4 units / 0.2 s = 20 units/s (players run at 30) */
    fuelKindleMs: 150,

    fuel: { burnMs: 6000, evaporateMs: 60_000 },
    grass: { burnMs: 3200, spreadChance: 0.3 },
    bush: { burnMs: 4500, extraBudget: 3 },
    molotov: { radius: 10, burnMs: 7000, grassBudget: 12 },
    /** explosions light poured fuel inside this share of their radius; barrel explosions also light a little grass */
    explosionFuelRadius: 0.6,
    barrel: { radius: 6, burnMs: 5000, grassBudget: 8 },

    damage: {
        /** in the flames: ramps from minDps to maxDps over rampMs of contact, resets after rampResetMs out of it */
        minDps: 10,
        maxDps: 20,
        rampMs: 1500,
        rampResetMs: 1000,
        /** after leaving the flames you keep burning */
        burnDps: 4,
        burnMs: 3000,
        /** damage is dealt in pulses this far apart */
        pulseMs: 200
    },
    /** fire damage to flammable obstacles (per second) and to explosive barrels */
    obstacleDps: 12,
    barrelDps: 30,

    /** weather: spread chance and burn time multipliers, and how fast fuel evaporates */
    weather: {
        overcast: { spread: 1, duration: 1, evaporate: 1 },
        fog: { spread: 1, duration: 1, evaporate: 1 },
        drizzle: { spread: 0.5, duration: 0.75, evaporate: 0.75 },
        rain: { spread: 0, duration: 0.55, evaporate: 0.5 }
    }
});

export const enum FireCellState {
    None,
    Fuel,
    Kindling,
    Burning,
    Scorched
}

/** how a floor reacts to fire */
export const enum Flammability {
    /** water, void: nothing can burn here, fuel can't be poured here */
    None,
    /** sand, roads, stone, building floors: poured fuel burns, fire never spreads by itself */
    FuelOnly,
    /** grass fields: fire may spread using the limited budget of the fire that started it */
    Grass
}
